import React, { useCallback, useContext, useEffect, useState } from 'react';
import { render } from 'react-dom';
import { SWRConfig } from 'swr'
import {
    BrowserRouter,
    Route,
    Switch,
    Link, withRouter
} from 'react-router-dom';
import { useNavigate, useLocation, CompatRoute, CompatRouter } from "react-router-dom-v5-compat";
import ReactRouterPropTypes from 'react-router-prop-types';

import "../css/main.scss";
import "../css/select-box.css";
import "../css/react-table.css"

// TODO: after we update to react table v8, check if we can get rid of this
import "regenerator-runtime/runtime.js";

import i18next from 'i18next';
import XHR from 'i18next-http-backend';
import sprintf from 'i18next-sprintf-postprocessor';
import LanguageDetector from 'i18next-browser-languagedetector';
import axios from 'axios';

import { LoginComponent } from './LoginComponent.js';
import { TranslationContext } from "./hooks/useTranslationContext";
import { ApiConnectionContext, ApiConnectionContextProvider } from "./hooks/useApiConnectionContext";
// First we import some components...
import { MainComponent } from './main.js';

import { Om7Sense } from './Om7Sense';
import CustomChartComponent from './CustomChartLoader.jsx';
import { logError } from './util/error.js';
import { ErrorBoundary } from "react-error-boundary";

const p = Om7Sense;
window.p = p;

// Register application wide uncaught exception logger
window.onError = logError;

const dateFormatOptions =  { year: 'numeric', month: "numeric", day: 'numeric', "hour": "numeric", "minute": "numeric", "second": "numeric" };

const swrConfig = {
    fetcher: (resource, init) => fetch(resource, init).then(res => res.json())
};

i18next.use(XHR).use(LanguageDetector).use(sprintf).init({
    detection:{
        lookupQuerystring: 'lang',
        lookupCookie: 'lang',
        order: ['querystring','cookie','navigator']
    },
    ns: [ "theme", "translation" ],
    defaultNS: "theme",
    fallbackNS: "translation",
    debug:false,
    load: 'languageOnly',
    fallbackLng: 'en',
    interpolation: {
        format: function(value, format) {
            if (format === 'firstUpper') {
                return value.charAt(0).toUpperCase() + value.slice(1);
            }
            if (value instanceof Date) {
                return Intl.DateTimeFormat(navigator.language, dateFormatOptions).format(value)
            }
            return value;
        }
    }
}, function(err, t) {

    let apiError;

    // Load translations
    axios.get("/apiv2/translations").then(function (result) {
        i18next.addResourceBundle(i18next.language, 'translation', result.data[i18next.language]);
        // Add fallback
        i18next.addResourceBundle('en', 'translation', result.data.en);
 
        // Also set mapUnit in topic utils        
        require('./util/topic.js').init(t, result.data.en.mapUnit);
    }, function (err) {
        apiError = err;
    }).finally(() => {

        // Instantiate our custom chart loader
        CustomChartComponent(t, p);

        const MainErrorPage = ({ error }) => {
            return (
                <div className="wrapper devices">
                    <div className="connectionlost">
                        <div className="loader-element">
                            <div className="loading loading-lg"></div>
                        </div>
                        <br/>
                        <h4>{ t(error ? 'main.title_error' : 'state.disconnected') }</h4>
                        <h5>{ t(error ? 'main.desc_error' : 'state.disconnected_explanation') }</h5>

                    </div>
                </div>
            )
        };

        const MainFrame = () => {
            const [ apiError, setApiError ] = useState(null);
            const navigate = useNavigate();
            const location = useLocation();
            const { connected, logged_in_as } = useContext(ApiConnectionContext);

            const onLogin = useCallback(() => {
                if (location.pathname === "/login") {
                    if (location.state && location.state?.nextPathname !== "/") {
                        navigate(location.state?.nextPathname);
                    } else {
                        navigate("/devices");
                    }
                }
            }, [ navigate, location ]);

            const onLogout = useCallback(() => {
                console.trace("loggedOut");
                if (location.pathname !== "/login") {
                    navigate("/login", { nextPathname: location.pathname});
                }
            }, [ navigate, location ]);
            
            const onError = (error, errorInfo) => {
                // Log the error to our error reporting service into the backend
                logError("componentDidCatch index.jsx", error?.fileName, error?.lineNumber, error?.columnNumber, error, errorInfo?.componentStack);
            };

            // Set up API connection listeners
            useEffect(() => {
                p.addListener("authenticated", onLogin);
                p.addListener("not_authenticated", onLogout);

                if (p.isAuthenticated() || p.hasSessionId()) {
                    if (location.pathname === "/") {
                        if (location.state && location.state?.nextPathname !== "/") {
                            navigate(location.state?.nextPathname);
                        } else {
                            navigate("/devices");
                        }
                    }
                } else if (location.pathname.indexOf("/login") !== 0) {
                    navigate("/login", { nextPathname: location.pathname });
                }
            }, [ p, onLogin, onLogout, location, navigate ]);

            const handleLogout = useCallback(() => {
                p.closeSession();
            }, [ p ]);

            useEffect(() => {
                const { hash, pathname } = location;
                if (hash !== '') {
                    // Push onto callback queue so it runs after the DOM is updated,
                    // this is required when navigating from a different page so that
                    // the element is rendered on the page before trying to getElementById.
                    setTimeout(() => {
                        const id = hash.replace('#', '');
                        const element = document.getElementById(id);
                        if (element) {
                            element.scrollIntoView();
                        }
                    }, 0);
                    // Drop hash from URL
                    navigate(pathname);
                }
            }, [ location, navigate ]);

            try {
                if (!connected) {
                    return <MainErrorPage />;
                }
                return (
                    <ErrorBoundary
                            fallbackRender={MainErrorPage}
                            onError={onError}>
                        <Switch>
                            <Route path="/login">
                                <LoginComponent p={p} />
                            </Route>
                            <Route path="/">
                                <MainComponent />
                            </Route>
                        </Switch>
                    </ErrorBoundary>
                );
            } catch (error) {
                onError("componentDidCatch index.jsx", error?.fileName, error?.lineNumber, error?.columnNumber, error, errorInfo.componentStack);
            }
        }

        render(
            <TranslationContext.Provider value={t}>
                <ApiConnectionContextProvider p={p}>
                    <SWRConfig value={swrConfig}>
                        <BrowserRouter>
                            <CompatRouter>
                                <MainFrame />
                            </CompatRouter>
                        </BrowserRouter>
                    </SWRConfig>
                </ApiConnectionContextProvider>
            </TranslationContext.Provider>
        , document.getElementById("omsensemain"));


    });

});

